import { SmsLinkCallback } from './pages/authentication/SmsLinkCallback';
import { hostingPreviewForPath } from './pages/hosting/previews';
import { governancePreviewForPath } from './pages/governance/previews';
import { bankingPreviewForPath } from './pages/banking/previews';

/**
 * Seamless-page-auth teasers (vault/how-to/seamless-page-auth-pattern.md § Part 3).
 * A module registers its own `previews.ts` here; ProtectedRoute never changes.
 */
const ROUTE_PREVIEWS: Record<string, (path: string) => { title: string; blurb: string; node?: React.ReactNode } | undefined> = {
  hosting: hostingPreviewForPath,
  governance: governancePreviewForPath,
  banking: bankingPreviewForPath,
};
import { AuthProfilePrompt } from './pages/authentication/components/AuthProfilePrompt';
import { EmailLinkCallback } from "./pages/authentication/EmailLinkCallback";
import React, { Suspense } from "react";
import { ChakraProvider, Spinner, Center } from "@chakra-ui/react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import LegacyTenantRedirect from "./components/LegacyTenantRedirect";
import { ApolloProvider } from "@apollo/client";
import { apolloClient } from "./apollo/client";
import Home from "./pages/Home";
import LaunchModuleUpdate from "./pages/updates/LaunchModule";
import HQAutopilot from "./pages/hqautopilot/HQAutopilot";
import SafeCryptoCustody from "./pages/updates/SafeCryptoCustody";
import SafeCryptoCompetition from "./pages/updates/SafeCryptoCompetition";
import LaunchSiteManagement from "./pages/updates/LaunchSiteManagement";
import LaunchDomainLifecycle from "./pages/updates/LaunchDomainLifecycle";
import CalendarBroadcastUpgrade from "./pages/updates/CalendarBroadcastUpgrade";
import ModuleTransitionsForSurgReady from "./pages/updates/ModuleTransitionsForSurgReady";
import LMAttachmentUpgrade from "./pages/updates/LMAttachmentUpgrade";
import TalentLinkPlusLaunch from "./pages/updates/TalentLinkPlusLaunch";
import LeadFlowEmailBranding from "./pages/updates/LeadFlowEmailBranding";
import cryptoCustodyModuleConfig from "./pages/crypto-custody/moduleConfig";
const CryptoCustodyDashboard = React.lazy(() => import("./pages/crypto-custody/Dashboard"));
import LoginPage from "./pages/authentication/LoginPage";
import Doctorate from "./pages/Doctorate";
import BdayPage from "./pages/bday";
import BdayRsvpPage from "./pages/bday/rsvp";
import BdaySpeechPage from "./pages/bday/speech";
import OneGroupProposal from "./pages/offers/onegroup";
import WattDropProposal from "./pages/offers/wattdrop";
import PaulNiedererProposal from "./pages/offers/paulniederer";
// import OneGroupStructuredProposal from "./pages/offers/onegroupstructured";
import { brandConfig } from "./brandConfig";
import theme from "./theme";

// Module configurations
import clientsModuleConfig from "./pages/clients/moduleConfig";
import passwordsModuleConfig from "./pages/passwords/moduleConfig";
import employeesModuleConfig from "./pages/employees/moduleConfig";
import companiesModuleConfig from "./pages/companies/moduleConfig";
import seoMetadataModuleConfig from "./pages/seometadata/moduleConfig";
import CompaniesList from "./pages/companies/CompaniesList";
import NewCompany from "./pages/companies/NewCompany";
import CompanyDetails from "./pages/companies/CompanyDetails";
import EditCompany from "./pages/companies/EditCompany";
import assetsModuleConfig from "./pages/assets/moduleConfig";
import { calendarsModuleConfig } from "./pages/calendars/moduleConfig";
import opportunitiesModuleConfig from "./pages/opportunities/moduleConfig";
import projectsModuleConfig from "./pages/projects/moduleConfig";
import emailsModuleConfig from "./pages/emails/moduleConfig";
import commsHealthModuleConfig from "./pages/comms-health/moduleConfig";
import meetModuleConfig from "./pages/meet/moduleConfig";
import pokerCoachModuleConfig from "./pages/poker-coach/moduleConfig";
import club52ModuleConfig from "./pages/club52/moduleConfig";
import smsModuleConfig from "./pages/sms/moduleConfig";
import commsLogModuleConfig from "./pages/comms-log/moduleConfig";
import transcriptionsModuleConfig from "./pages/transcriptions/moduleConfig";
import proposalsModuleConfig from "./pages/proposals/moduleConfig";
import companyKnowledgeModuleConfig from "./pages/companyknowledge/moduleConfig";
import knowledgeExplorerModuleConfig from "./pages/knowledgeexplorer/moduleConfig";
import artifactsModuleConfig from "./pages/artifacts/moduleConfig";
import publicTrustModuleConfig from "./pages/public-trust/moduleConfig";
import hostingModuleConfig from "./pages/hosting/moduleConfig";
import lmModuleConfig from "./pages/lm/moduleConfig";
import frontendUpgradesModuleConfig from "./pages/frontend-upgrades/moduleConfig";
import modulesModuleConfig from "./pages/modules/moduleConfig";
import siteEditorModuleConfig from "./pages/site-editor/moduleConfig";
import masterSiteEditorModuleConfig from "./pages/master-site-editor/moduleConfig";
import analyticsModuleConfig from "./pages/analytics/moduleConfig";
import mediaCDNModuleConfig from "./pages/media-cdn/moduleConfig";
import transcriptMinerModuleConfig from "./pages/transcript-miner/moduleConfig";
import vlogModuleConfig from "./pages/vlog/moduleConfig";
import stateOfPlayModuleConfig from "./pages/state-of-play/moduleConfig";
import sovereigntyModuleConfig from "./pages/sovereignty/moduleConfig";
import valueLiteracyModuleConfig from "./pages/value-literacy/moduleConfig";
import governanceModuleConfig from "./pages/governance/moduleConfig";
import agentBoardModuleConfig from "./pages/agent-board/moduleConfig";
import bankingModuleConfig from "./pages/banking/moduleConfig";
import walletModuleConfig from "./pages/wallet/moduleConfig";
const WalletInvitePage = React.lazy(() => import("./pages/wallet/WalletInvitePage"));
import knowledgeGraphModuleConfig from "./pages/knowledge-graph/moduleConfig";
// autoDjiUploadModuleConfig removed - module not yet created
import bdayModuleConfig from "./pages/bday/moduleConfig";
import zkAuthenticationModuleConfig from "./pages/zk-authentication/moduleConfig";
import pModuleConfig from "./pages/p/moduleConfig";
import profileModuleConfig from "./pages/profile/moduleConfig";
import rewardsModuleConfig from "./pages/rewards/moduleConfig";
import growthEngineModuleConfig from "./pages/growth-engine/moduleConfig";
import seamlessChatModuleConfig from "./pages/seamless-chat/moduleConfig";
import launchModuleConfig from "./pages/launch/moduleConfig";
import tomModuleConfig from "./pages/tom/moduleConfig";
import leadflowModuleConfig from "./pages/leadflow/moduleConfig";
import energyTrackerModuleConfig from "./pages/energy-tracker/moduleConfig";
import propertyIntelModuleConfig from "./pages/property-intel/moduleConfig";
// Renamed tom-manager → client-journey (v2.0). Folder kept; id is now "client-journey".
import clientJourneyModuleConfig from "./pages/tom-manager/moduleConfig";
import vaultInsightsModuleConfig from "./pages/vault-insights/moduleConfig";
// subscriptionBillingModuleConfig removed — merged into tenant-management module
// videoServerModuleConfig removed - module deleted

/*
 * MIGRATION NOTE: We are transitioning to a dynamic module routing system.
 * 
 * The Clients module has been converted as the example pattern.
 * As we update each module, we will:
 * 1. Create/update its moduleConfig.ts file (see clients/moduleConfig.ts as example)
 * 2. Import the moduleConfig here
 * 3. Add it to the modules array below
 * 4. Remove its hardcoded routes from below
 * 
 * This allows modules to be truly plug-and-play with automatic route registration
 * and centralized permission management.
 * 
 * TO CONVERT: Projects, Bills, Products, Sessions, Emails, etc.
 */

// Import other module configs here as you convert them:
import billsModuleConfig from "./pages/bills/moduleConfig";
import tenantManagementModuleConfig from "./pages/tenant-management/moduleConfig";
import moduleUpgradeModuleConfig from "./pages/module-upgrade/moduleConfig";
import aiOpsModuleConfig from "./pages/ai-ops/moduleConfig";
import ipfsManagementModuleConfig from "./pages/ipfs-management/moduleConfig";
import adminModuleConfig from "./pages/admin/moduleConfig";
import p2ModuleConfig from "./pages/p2/moduleConfig";
import dataexportModuleConfig from "./pages/dataexport/moduleConfig";

// Combine all module configurations
import phoneSystemModuleConfig from "./pages/phone-system/moduleConfig";

const modules = [
  phoneSystemModuleConfig,
  modulesModuleConfig, // Public modules directory - always first for easy discovery
  clientsModuleConfig,
  passwordsModuleConfig,
  employeesModuleConfig,
  companiesModuleConfig,
  seoMetadataModuleConfig, // SEO metadata module with ADMIN/ADMIN_DASHBOARD permissions
  assetsModuleConfig,
  calendarsModuleConfig,
  opportunitiesModuleConfig,
  proposalsModuleConfig, // Proposals module with PROPOSALS_VIEW/CREATE/ADMIN permissions
  billsModuleConfig,  // Now using dynamic routing!
  transcriptionsModuleConfig, // Transcriptions module with TRANSCRIPTION_USER/ADMIN permissions
  projectsModuleConfig, // Projects module with PROJECTS_ADMIN permission
  emailsModuleConfig, // Emails module with EMAIL_ADMIN and EMAIL_USER permissions
  commsHealthModuleConfig, // Email Health — loop-back deliverability probes + config checks + weekly report
  meetModuleConfig, // Meet — self-hosted LiveKit video meetings; public /meet/:room join page
  pokerCoachModuleConfig,
  club52ModuleConfig, // Club52 — poker game organiser (Block52); public /club52/:room join page

  smsModuleConfig, // SMS module (Cellcast) — inbox, send, inbound notifications. moduleId "sms"
  commsLogModuleConfig, // Outgoing Comms — this site's outbound SMS + email, provider truth, tenant-isolated (COMMS_LOG_VIEW / COMMS_LOG_ADMIN)
  companyKnowledgeModuleConfig, // Company knowledge base with COMPANY_KNOWLEDGE_* permissions
  knowledgeExplorerModuleConfig, // Knowledge Explorer — browse + chat over the Mac Studio synced markdown folder (runbook 43)
  publicTrustModuleConfig, // Public Trust — people in public life + sourced activity log, one private calendar each
  artifactsModuleConfig, // Artifacts — publish shareable docs + Mermaid (Claude-agnostic); public /a/:token share page
  hostingModuleConfig, // Hosting — build-page deploys, offers + 20% affiliate ledger; public /hosting/:slug landing
  lmModuleConfig, // Learning Management System with course creation and enrollment
  frontendUpgradesModuleConfig, // Frontend upgrade tracking with WEBSITE_MASTER permission
  tenantManagementModuleConfig, // Tenant management with ADMIN/TENANT_MASTER_ADMIN permissions
  moduleUpgradeModuleConfig, // Module Upgrade Service — Treehouse-worker cockpit, separate sibling of ModuleSync (runbook 52)
  aiOpsModuleConfig, // AI Ops — Mac Studio usage/telemetry/virtual-keys cockpit (runbook 54)
  ipfsManagementModuleConfig, // Private IPFS fleet — storage, routing, failover
  adminModuleConfig, // System administration with ADMIN/TENANT_MASTER_ADMIN permissions
  siteEditorModuleConfig, // Site Editor - Tenant self-service website editing with Claude
  masterSiteEditorModuleConfig, // Master Site Editor - Manage all tenant websites (SITE_EDITOR_MASTER_ADMIN)
  analyticsModuleConfig, // Analytics - Privacy-first website analytics
  mediaCDNModuleConfig, // Media CDN - File hosting with IPFS storage
  transcriptMinerModuleConfig, // Transcript Miner - YouTube video archive + transcripts
  vlogModuleConfig, // Vlog - shoot → drag → live public page
  stateOfPlayModuleConfig, // State of Play - short recorded audio events with public replay pages
  sovereigntyModuleConfig, // Client sovereignty journey - staff ledger and human-led progress
  valueLiteracyModuleConfig,
  governanceModuleConfig, // Governance - organisations, voting, work→proof→tokens
  agentBoardModuleConfig, // Agent Board - Claude Code job slots, queue and review (2026-10-22)
  bankingModuleConfig, // Banking - live balances, transactions, alerts and digests (Redbark)
  walletModuleConfig, // Wallet - signing keys as a history, EIP-712 signatures, address book
  knowledgeGraphModuleConfig, // Knowledge Graph - free-energy network extraction (admin)
  // autoDjiUploadModuleConfig removed - module not yet created
  bdayModuleConfig, // Birthday RSVP - Guest management for Tom's 40th
  zkAuthenticationModuleConfig, // ZK Biometric Auth - Face-based passwordless login
  pModuleConfig, // Proposals v2 - Enhanced proposal management with opportunity linking
  rewardsModuleConfig, // BB Token rewards - issue, track, and redeem tokens
  growthEngineModuleConfig, // Growth Engine - Claude-powered tenant motivation system
  seamlessChatModuleConfig, // Seamless Chat - Zero-friction anonymous chat rooms
  launchModuleConfig, // Launch - Create and manage tenant websites with AI-assisted setup
  tomModuleConfig, // Tom AI Assistant - Floating AI panel for natural language business automation
  cryptoCustodyModuleConfig, // Crypto Custody - Time-locked vaults with validator network
  leadflowModuleConfig, // LeadFlow - Lead capture forms, pipeline management, marketing
  energyTrackerModuleConfig, // Energy Tracker - National fuel supply chain map with REA value flows
  propertyIntelModuleConfig, // Property Intel - Brisbane property heat-map (CC-BY 4.0 government data)
  p2ModuleConfig, // P2 Marketplace - Group Buy with pledge campaigns, messaging, delivery tracking
  clientJourneyModuleConfig, // Client Journey Cockpit — chase configs, journey milestones, daily drafts
  vaultInsightsModuleConfig, // Vault Insights - Scheduled AI-curated digests rendered from the Obsidian vault
  profileModuleConfig, // Profile management — view, edit, settings, shipping, payments, subscriptions
  dataexportModuleConfig, // Export my data — per-module / everything CSV downloads at /settings/export-data
  // Add other modules here as you convert them:
];

// Protected Route Component
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ModulePageLayout } from "./components/ModulePageLayout";
import { DocumentTitleManager } from "./components/DocumentTitleManager";
import { SecurityPage } from "./pages/authentication";
import OnboardingPage from "./pages/onboarding/OnboardingPage";

// Public chat page (no auth required)
const PublicChat = React.lazy(() => import("./pages/seamless-chat/PublicChat"));
const FloatingChatWidget = React.lazy(() => import("./components/floating-chat/FloatingChatWidget"));

// Public terms page (no auth)
const TermsPage = React.lazy(() => import("./pages/terms/TermsPage"));
// Public ownership ladder page (no auth) — the published price of every exit
const OwnershipPage = React.lazy(() => import("./pages/Ownership"));
// Public module register (no auth) — the live availableModules list, grouped by tier
const ModulesPage = React.lazy(() => import("./pages/Modules"));
const PrivacyPage = React.lazy(() => import("./pages/privacy/PrivacyPage"));
// Public "The journey" placeholder (The Program links here)
const JourneyPage = React.lazy(() => import("./pages/journey/JourneyPage"));

// Public artifact share page (no auth — token-gated)
const PublicArtifact = React.lazy(() => import("./pages/artifacts/PublicArtifact"));

// Public P2 pages (no auth required)
const P2PublicCampaignPage = React.lazy(() => import("./pages/p2/components/P2PublicCampaignPage"));
const P2Onboard = React.lazy(() => import("./pages/p2/components/P2Onboard"));
const P2SalesPage = React.lazy(() => import("./pages/p2/components/P2SalesPage"));

// Loading component for lazy-loaded routes
const LoadingFallback = () => (
  <Center h="100vh">
    <Spinner size="xl" color={brandConfig.colors.primary} />
  </Center>
);

// Launch module — handled by launchModuleConfig in modules array

// Bill-related imports (moved to dynamic routing via billsModuleConfig)
// Special bill pages that aren't in the module
import BillChaserPage from "./pages/admin/billChaserPage";




// Project-related imports - Now handled by projectsModuleConfig

// Email-related imports - Now handled by emailsModuleConfig

// Calendar-related imports
import CalendarAccountsAdmin from "./pages/calendars/CalendarAccountsAdmin";

// Knowledge Base imports
// KnowledgeBase imports removed - module deleted

// Subscription/Billing imports removed — consolidated into bills module (billsModuleConfig)

// Admin and dashboard imports
import AdminDashboard from "./pages/AdminDashboard";
import WhatsNew from "./pages/admin/WhatsNew";

// Phone System imports

// Test and development imports
import SendTestEmail from "./pages/test/SendTestEmail";
import TestUploadFile from "./pages/test/TestUploadFile";
import TestClientCart from "./pages/test/TestClientCart";
import FoundationOverview from "./pages/cheatsheets/FoundationOverview";
import CheatsheetIndex from "./pages/cheatsheets/CheatsheetIndex";
import SubscriptionLmPattern from "./pages/cheatsheets/SubscriptionLmPattern";
import SurgreadyPatientJourney from "./pages/cheatsheets/SurgreadyPatientJourney";
import SubscriptionRewardsPattern from "./pages/cheatsheets/SubscriptionRewardsPattern";
import SeamlessChatModule from "./pages/cheatsheets/SeamlessChatModule";
import AiAssistantCheatsheet from "./pages/cheatsheets/AiAssistantCheatsheet";
import EnergyResearchIndex from "./pages/energy-research";
import AustraliaKeyLeaders from "./pages/energy-research/AustraliaKeyLeaders";
import AustraliaIsraelSupport from "./pages/energy-research/AustraliaIsraelSupport";
import AustraliaImmediateActions from "./pages/energy-research/AustraliaImmediateActions";
import AustraliaOilReserves from "./pages/energy-research/AustraliaOilReserves";
import AustraliaOilSources from "./pages/energy-research/AustraliaOilSources";
import AustraliaOilReservesByState from "./pages/energy-research/AustraliaOilReservesByState";
import AustraliaF35Companies from "./pages/energy-research/AustraliaF35Companies";
import JapanEnergyCrisis from "./pages/energy-research/JapanEnergyCrisis";
import AustraliaDecisionMakers from "./pages/energy-research/AustraliaDecisionMakers";
import JapanDecisionMakers from "./pages/energy-research/JapanDecisionMakers";
import KeyRefineries from "./pages/energy-research/KeyRefineries";
import AlternativeSupplyRoutes from "./pages/energy-research/AlternativeSupplyRoutes";
import RelationshipMap from "./pages/energy-research/RelationshipMap";
import ResearchChecklist from "./pages/energy-research/ResearchChecklist";
import IranHormuzUpdate from "./pages/energy-research/IranHormuzUpdate";


import Event1Page from "./pages/events/tom/1";
import Event2Page from "./pages/events/tom/2";
import Event3Page from "./pages/events/tom/3";
import Event4Page from "./pages/events/tom/4";
import Block52EventPage from "./pages/events/tom/Block52";
import ChrisEventPage from "./pages/events/tom/chris";

// Contexts and Layout
import { AuthProvider } from "./contexts/AuthContext";
import { ModulePermissionsProvider, useModulePermissions } from "./contexts/ModulePermissionsContext";
import { IpfsGatewayLoader } from "./components/IpfsGatewayLoader";
import { TenantBrandingLoader } from "./components/TenantBrandingLoader";
// Tenant management components now loaded via tenantManagementModuleConfig (dynamic routing)

// Profile routes — now handled by profileModuleConfig (dynamic routing with useLayout)

// Seller Profile imports
import NewSellerProfile from "./pages/sellerprofile/NewSellerProfile";
import SellerProfilesList from "./pages/sellerprofile/SellerProfilesList";
import SellerProfileView from "./pages/sellerprofile/SellerProfileView";

// import ShippingAddresses from "./pages/profile/ShippingAddresses";

// Permissions Matrix import
import Permissions from "./pages/Permissions";
// import PaymentMethods from "./pages/profile/PaymentMethods";

// Domains module imports
import { DomainsPage } from "./pages/domains/index";
import { DomainDetails } from "./pages/domains/DomainDetails";

// Offers module imports
import { MaintenanceOffer } from "./pages/offers/onegroup/maintenance";
import SiteMaintenance from "./pages/offers/onegroup/sitemaintenance";

// Public LeadFlow forms (NO AUTH REQUIRED)
import PublicForm from "./pages/leadflow/PublicForm";
// Public Booking imports (NO AUTH REQUIRED)
import PublicBookingPage from "./pages/calendars/booking/PublicBookingPage";
import PublicBookingEventPage from "./pages/calendars/booking/PublicBookingEventPage";
import PublicBookingConfirmPage from "./pages/calendars/booking/PublicBookingConfirmPage";
import PublicBookingSuccessPage from "./pages/calendars/booking/PublicBookingSuccessPage";
import ManageBooking from "./pages/calendars/ManageBooking";

// Business Builder imports (NO AUTH REQUIRED - Public Pages)
import BusinessBuilder from "./pages/businessbuilder/index";
import Tokenomics from "./pages/businessbuilder/Tokenomics";
import Governance from "./pages/businessbuilder/Governance";
import Treasury from "./pages/businessbuilder/Treasury";
import Marketplace from "./pages/businessbuilder/Marketplace";
import TithingCalculator from "./pages/businessbuilder/TithingCalculator";
import Rewards from "./pages/businessbuilder/Rewards";
import Journey from "./pages/businessbuilder/Journey";
import Interest from "./pages/businessbuilder/Interest";
import PartnersDirectory from "./pages/partners/PartnersDirectory";
// Leaderboard is now part of rewards module (public route at /rewards/leaderboard)

// Live Event imports (NO AUTH REQUIRED - Public Pages)
import LiveEventInterest from "./pages/live-event/InterestPage";
import LiveEventRegister from "./pages/live-event/RegisterPage";
import LiveEventLive from "./pages/live-event/LivePage";
import LiveEventPurchase from "./pages/live-event/PurchasePage";
import LiveEventAdmin from "./pages/live-event/AdminPage";
import LiveEventRegistrations from "./pages/live-event/RegistrationsPage";

// Floating navbar component
import { FloatingNavbar } from "./components/FloatingNavbar";

// Tom AI Assistant
import { TomProvider } from "./pages/tom/TomProvider";
// const TomPanel = React.lazy(() => import("./pages/tom/TomPanel")); // disabled — see AppContent
import { useAuth } from "./contexts/AuthContext";

// Analytics tracker - tracks page views and engagement
import { AnalyticsTracker } from "./pages/analytics/components/AnalyticsTracker";


/**
 * Browser-tab <Page> names for the HAND-WRITTEN routes in <Routes> below. Module
 * routes need nothing here — DocumentTitleManager titles them from their
 * moduleConfig. A page's own usePageTitle() still wins; "" = the site name alone.
 * Standard: vault/how-to/how-to-build-new-module.md § "Browser tab titles".
 */
const STATIC_ROUTE_TITLES: Record<string, string> = {
  "/": "",
  "/login": "Sign in",
  "/auth/sms-link": "Signing in",
  "/auth/email-link": "Signing in",
  "/security": "Security",
  "/onboarding": "Onboarding",
  "/terms": "Terms",
  "/ownership": "Ownership",
  "/modules": "Modules",
  "/privacy": "Privacy",
  "/journey": "Journey",
  "/updates/*": "Update",
  "/doctorate": "Doctorate",
  "/bday": "Birthday",
  "/bday/40years": "Birthday",
  "/bday/40years/:slug": "RSVP",
  "/bday/speech": "Speech",
  "/businessbuilder": "Business Builder",
  "/businessbuilder/tokenomics": "Tokenomics",
  "/businessbuilder/governance": "Governance",
  "/businessbuilder/treasury": "Treasury",
  "/businessbuilder/marketplace": "Marketplace",
  "/businessbuilder/tithing-calculator": "Tithing Calculator",
  "/businessbuilder/rewards": "Rewards",
  "/businessbuilder/journey": "Journey",
  "/businessbuilder/interest": "Register Interest",
  "/partners": "Partners",
  "/hq-autopilot": "HQ Autopilot",
  "/live-event/*": "Live Event",
  "/forms/:slug": "Form",
  "/book/:slug": "Book a time",
  "/book/:slug/:eventTypeId": "Book a time",
  "/book/:slug/confirm": "Confirm booking",
  "/book/:slug/success": "Booking confirmed",
  "/calendars/booking/manage/:token": "Manage booking",
  "/c/:slug": "Chat",
  "/a/:token": "Shared artifact",
  "/wallet/invite/:slug": "Wallet invite",
  "/p2/c/:slug": "Campaign",
  "/p2/onboard/:slug": "Onboarding",
  "/become-a-campaign-manager": "Become a campaign manager",
  "/permissions": "Permissions",
  "/admin/whatsnew/:moduleId": "What's New",
  "/offers/*": "Offer",
  "/test/*": "Test",
  "/cheatsheets": "Cheatsheets",
  "/cheatsheets/*": "Cheatsheet",
  "/business-builder/cheatsheets": "Cheatsheets",
  "/business-builder/cheatsheets/*": "Cheatsheet",
  "/energy-research": "Energy Research",
  "/energy-research/*": "Energy Research",
};

const AppContent = () => {
  const { isAuthenticated, user } = useAuth();
  const { permissions: backendPerms } = useModulePermissions();

  // Resolve permissionLevel → actual permission strings from backend
  // Modules using the new pattern have permissionLevel on routes/nav and empty permission arrays.
  // Modules not yet migrated still have hardcoded permission arrays — those pass through unchanged.
  const resolvedModules = React.useMemo(() => {
    return modules.map(module => {
      const modulePerms = backendPerms[module.id];
      if (!modulePerms) return module; // Not migrated yet — use hardcoded permissions

      return {
        ...module,
        routes: module.routes?.map((route: any) => {
          const level = (route as any).permissionLevel;
          if (!level) return route; // No permissionLevel — use existing permissions array
          return { ...route, permissions: (modulePerms as any)[level] || [] };
        }),
        navigation: module.navigation?.map((navItem: any) => {
          const level = (navItem as any).permissionLevel;
          if (!level) return navItem; // No permissionLevel — use existing permissions array
          return { ...navItem, permissions: (modulePerms as any)[level] || [] };
        }),
        permissions: {
          ...module.permissions,
          view: modulePerms.view || module.permissions.view,
          create: modulePerms.create || module.permissions.create,
          edit: modulePerms.edit || module.permissions.edit,
          delete: modulePerms.delete || module.permissions.delete,
          ...(modulePerms.financial ? { financial: modulePerms.financial } : {}),
        },
      };
    });
  }, [backendPerms]);

  return (
    <>
      {/* Analytics tracking - runs on every page view */}
      <AnalyticsTracker enabled={true} />

      {/* Show floating navbar only when authenticated */}
      {isAuthenticated && <FloatingNavbar modules={resolvedModules} />}

      {/* Floating Knowledge Explorer chat — same conversation + tools as the
          /knowledgeexplorer page (session piggyback via localStorage), on every
          authenticated page. Gating mirrors the KE module's own FE config
          (open to any logged-in user; the REAL gate is the backend's
          MASTER_KNOWLEDGE check on every request — a user without it gets a
          clear error on first send, same as visiting the page). */}
      {/* Mounted only once the client id is KNOWN, and keyed by it (2026-09-07).
          The widget persists open/position/size under `floatingchat.*.<clientId>`
          and reads them in useState initialisers — on a page refresh it mounted
          while `user` was still loading, read the "guest" keys, and never re-read,
          so the panel came back closed at its default spot every time. */}
      {isAuthenticated && ((user as any)?.clientId || (user as any)?.client?.id || (user as any)?.id) && (
        <React.Suspense fallback={null}>
          <FloatingChatWidget
            key={String((user as any)?.clientId || (user as any)?.client?.id || (user as any)?.id)}
            clientId={(user as any)?.clientId || (user as any)?.client?.id || (user as any)?.id}
          />
        </React.Suspense>
      )}

      {/* Tom AI Assistant - floating panel — DISABLED 2026-08-07 (Tom's request).
          Re-enable by uncommenting; TomProvider stays mounted below so other
          consumers (e.g. emails/EmailAiTrainer) keep working. */}
      {/* {isAuthenticated && (
        <React.Suspense fallback={null}>
          <TomPanel />
        </React.Suspense>
      )} */}

      {/* Each page now includes its own NavbarWithCallToAction and FooterWithFourColumns for consistency */}
      {/* Browser tab = "<Page> | <Site>" for every route — the ONE document.title writer. */}
      <DocumentTitleManager modules={resolvedModules} staticTitles={STATIC_ROUTE_TITLES}>
      <Routes>
        {/* Home page */}
        <Route path="/" element={<Home />} />

        {/* Login page */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/sms-link" element={<SmsLinkCallback />} />
        <Route path="/auth/email-link" element={<EmailLinkCallback />} />

        {/* Security & sign-in management — any logged-in user */}
        <Route path="/security" element={<ProtectedRoute requiredPermissions={[]}><SecurityPage /></ProtectedRoute>} />

        {/* Onboarding — staff with the ONBOARDING permission */}
        <Route path="/onboarding" element={<ProtectedRoute requiredPermissions={["ONBOARDING"]}><OnboardingPage /></ProtectedRoute>} />

        {/* Working-with-TMS terms — public */}
        <Route path="/terms" element={<React.Suspense fallback={<LoadingFallback />}><TermsPage /></React.Suspense>} />

        {/* Ownership ladder — public */}
        <Route path="/ownership" element={<React.Suspense fallback={<LoadingFallback />}><OwnershipPage /></React.Suspense>} />

        {/* Module register — public */}
        <Route path="/modules" element={<React.Suspense fallback={<LoadingFallback />}><ModulesPage /></React.Suspense>} />

        {/* Privacy policy — public */}
        <Route path="/privacy" element={<React.Suspense fallback={<LoadingFallback />}><PrivacyPage /></React.Suspense>} />

        {/* The journey — public placeholder */}
        <Route path="/journey" element={<React.Suspense fallback={<LoadingFallback />}><JourneyPage /></React.Suspense>} />

        {/* Logo concepts page */}

        {/* Updates pages */}
        <Route path="/updates/launch-module" element={<LaunchModuleUpdate />} />
        <Route path="/updates/safe-crypto-custody" element={<SafeCryptoCustody />} />
        <Route path="/updates/safe-crypto-competition" element={<SafeCryptoCompetition />} />
        <Route path="/updates/launch-site-management" element={<LaunchSiteManagement />} />
        <Route path="/updates/launch-domain-lifecycle" element={<LaunchDomainLifecycle />} />
        <Route path="/updates/calendar-broadcast-upgrade" element={<CalendarBroadcastUpgrade />} />
        <Route path="/updates/module-transitions" element={<ModuleTransitionsForSurgReady />} />
        <Route path="/updates/lm-attachment-upgrade" element={<LMAttachmentUpgrade />} />
        <Route path="/updates/talent-link-plus-launch" element={<TalentLinkPlusLaunch />} />
        <Route path="/updates/leadflow-email-branding" element={<LeadFlowEmailBranding />} />
        <Route path="/crypto-custody" element={<React.Suspense fallback={<Center h="60vh"><Spinner size="xl" /></Center>}><CryptoCustodyDashboard /></React.Suspense>} />

        {/* Doctorate announcement page */}
        <Route path="/doctorate" element={<Doctorate />} />

        {/* 🎂 40th Birthday Invitation Page (NO AUTH REQUIRED - Public Page) */}
        <Route path="/bday" element={<BdayPage />} />
        <Route path="/bday/40years" element={<BdayPage />} />
        <Route path="/bday/40years/:slug" element={<BdayRsvpPage />} />
        <Route path="/bday/speech" element={<BdaySpeechPage />} />

        {/* 🚀 BUSINESS BUILDER PAGES (NO AUTH REQUIRED - Public Pages) */}
        <Route path="/businessbuilder" element={<BusinessBuilder />} />
        <Route path="/businessbuilder/tokenomics" element={<Tokenomics />} />
        <Route path="/businessbuilder/governance" element={<Governance />} />
        <Route path="/businessbuilder/treasury" element={<Treasury />} />
        <Route path="/businessbuilder/marketplace" element={<Marketplace />} />
        <Route path="/businessbuilder/tithing-calculator" element={<TithingCalculator />} />
        <Route path="/businessbuilder/rewards" element={<Rewards />} />
        <Route path="/businessbuilder/journey" element={<Journey />} />
        <Route path="/businessbuilder/interest" element={<Interest />} />
        <Route path="/partners" element={<PartnersDirectory />} />
        <Route path="/hq-autopilot" element={<HQAutopilot />} />
        {/* Leaderboard handled by rewards module config (public route) */}

        {/* 🎁 REWARDS MODULE - Routes handled by rewardsModuleConfig in modules array */}

        {/* 🎤 LIVE EVENT PAGES (NO AUTH REQUIRED - Public Webinar Funnel) */}
        <Route path="/live-event/interest" element={<LiveEventInterest />} />
        <Route path="/live-event/register" element={<LiveEventRegister />} />
        <Route path="/live-event/live" element={<LiveEventLive />} />
        <Route path="/live-event/purchase" element={<LiveEventPurchase />} />
        <Route path="/live-event/admin" element={<LiveEventAdmin />} />
        <Route path="/live-event/registrations" element={<LiveEventRegistrations />} />

        {/* 📝 PUBLIC LEADFLOW FORMS (NO AUTH REQUIRED) */}
        {/* These routes allow public visitors to fill in LeadFlow forms without logging in */}
        <Route path="/forms/:slug" element={<PublicForm />} />

        {/* 📅 PUBLIC BOOKING PAGES (NO AUTH REQUIRED) */}
        {/* These routes allow public visitors to book appointments without logging in */}
        <Route path="/book/:slug" element={<PublicBookingPage />} />
        <Route path="/book/:slug/:eventTypeId" element={<PublicBookingEventPage />} />
        <Route path="/book/:slug/confirm" element={<PublicBookingConfirmPage />} />
        <Route path="/book/:slug/success" element={<PublicBookingSuccessPage />} />
        <Route path="/calendars/booking/manage/:token" element={<ManageBooking />} />

        {/* PUBLIC CHAT (NO AUTH REQUIRED) */}
        <Route path="/c/:slug" element={<React.Suspense fallback={<LoadingFallback />}><PublicChat /></React.Suspense>} />
        <Route path="/a/:token" element={<React.Suspense fallback={<LoadingFallback />}><PublicArtifact /></React.Suspense>} />

        {/* 🔑 PUBLIC WALLET INVITE (NO AUTH REQUIRED)
            An address-book invite is opened by somebody with no account here, so
            this is a bare route rather than one of the wallet module's own —
            those assume a permissionLevel. The server resolves the tenant from
            the INVITE RECORD, never from session context, because a logged-out
            visitor's x-tenant-id is whatever their browser happened to send. */}
        <Route path="/wallet/invite/:slug" element={<React.Suspense fallback={<LoadingFallback />}><WalletInvitePage /></React.Suspense>} />

        {/* PUBLIC P2 PAGES (NO AUTH REQUIRED) */}
        <Route path="/p2/c/:slug" element={<React.Suspense fallback={<LoadingFallback />}><P2PublicCampaignPage /></React.Suspense>} />
        <Route path="/p2/onboard/:slug" element={<React.Suspense fallback={<LoadingFallback />}><P2Onboard /></React.Suspense>} />
        <Route path="/become-a-campaign-manager" element={<React.Suspense fallback={<LoadingFallback />}><P2SalesPage /></React.Suspense>} />

        {/* Public Proposal Pages */}
        <Route path="/offers/onegroup" element={<OneGroupProposal />} />
        <Route path="/offers/wattdrop" element={<WattDropProposal />} />
        <Route path="/offers/paulniederer" element={<PaulNiedererProposal />} />
        {/* <Route path="/offers/onegroupstructured" element={<OneGroupStructuredProposal />} /> */}

        {/* Event routes */}
        <Route path="/events/tom/1" element={<Event1Page />} />
        <Route path="/events/tom/2" element={<Event2Page />} />
        <Route path="/events/tom/3" element={<Event3Page />} />
        <Route path="/events/tom/4" element={<Event4Page />} />
        <Route path="/events/tom/block52" element={<Block52EventPage />} />
        <Route path="/events/tom/chris" element={<ChrisEventPage />} />

        {/* Dynamic Module Routes — permissions resolved from backend for migrated modules */}
        {resolvedModules.map((module) =>
          module.routes?.map((route: any) => {
            const pageComponent = (
              <Suspense fallback={<LoadingFallback />}>
                <route.component />
              </Suspense>
            );

            // If useLayout is true, wrap with ModulePageLayout (auto Navbar + Breadcrumb + Container + Footer)
            const content = route.useLayout ? (
              <ModulePageLayout
                moduleConfig={module}
                {...(route.layoutOptions || {})}
              >
                {pageComponent}
              </ModulePageLayout>
            ) : pageComponent;

            return (
              <Route
                key={route.path}
                path={route.path}
                element={
                  (route as any).public ? (
                    content
                  ) : (
                    <ProtectedRoute requiredPermissions={route.permissions || []} preview={ROUTE_PREVIEWS[module.id]?.(route.path)}>
                      {content}
                    </ProtectedRoute>
                  )
                }
              />
            );
          })
        )}

        {/* Launch module routes — handled by launchModuleConfig in modules array */}

        {/* Bills module now uses dynamic routing - see billsModuleConfig */}
        {/* Special bill routes not in the module */}
        <Route path="/bill-chaser" element={<BillChaserPage />} />

        {/* Phone System routes */}


        {/* Project routes - Now handled by dynamic routing via projectsModuleConfig */}

        {/* Company routes */}
        <Route path="/companies" element={<CompaniesList />} />
        <Route path="/companies/new" element={<NewCompany />} />
        <Route path="/companies/:id" element={<CompanyDetails />} />
        <Route path="/companies/edit/:id" element={<EditCompany />} />

        {/* Email routes - Now handled by dynamic routing via emailsModuleConfig */}

        {/* Calendar routes */}
        <Route path="/calendar/admin/calendars" element={<CalendarAccountsAdmin />} /> {/* MASTER ADMIN: Manage all calendars */}

        {/* Knowledge Base routes - Admin only */}
        {/* KnowledgeBase routes removed - module deleted */}

        {/* Permissions Matrix */}
        <Route path="/permissions" element={<Permissions />} />

        {/* Subscription/Billing routes */}
        {/* Subscription routes removed — consolidated into bills module (billsModuleConfig handles /bills/subscriptions/*) */}

        {/* Admin routes */}
        <Route path="/admin" element={<AdminDashboard />} />

        {/* Tenant management routes - Now handled by dynamic routing via tenantManagementModuleConfig */}
        <Route path="/admin/whatsnew/:moduleId" element={<WhatsNew />} />

        {/* Profile routes — now handled dynamically by profileModuleConfig */}

        {/* Seller Profile routes */}
        <Route path="/seller-profiles" element={<SellerProfilesList />} />         {/* PUBLIC: View all seller profiles */}
        <Route path="/seller-profile/:id" element={<SellerProfileView />} />       {/* PUBLIC: View individual seller profile */}
        <Route path="/seller-profiles/new" element={<NewSellerProfile />} />       {/* USER: Create new seller profile */}


        {/* Domains module routes */}
        <Route path="/domains" element={<DomainsPage />} />  {/* USER: Domain search and management */}
        <Route path="/domains/:id" element={<DomainDetails />} />  {/* USER: Domain details and DNS management */}

        {/* Offers routes */}
        <Route path="/offers/maintenance" element={<MaintenanceOffer />} />  {/* PUBLIC: Business maintenance subscription offer */}
        <Route path="/offers/sitemaintenance" element={<SiteMaintenance />} />  {/* PUBLIC: Site maintenance subscription offer */}

        {/* Test routes */}
        <Route path="/test/sendemail" element={<SendTestEmail />} />
        <Route path="/test/upload" element={<TestUploadFile />} />
        <Route path="/test/cart" element={<TestClientCart />} />
        <Route path="/cheatsheets" element={<CheatsheetIndex />} />
        <Route path="/cheatsheets/foundation-overview" element={<FoundationOverview />} />
        <Route path="/cheatsheets/subscription-lm-pattern" element={<SubscriptionLmPattern />} />
        <Route path="/business-builder/cheatsheets" element={<CheatsheetIndex />} />
        <Route path="/business-builder/cheatsheets/foundation-overview" element={<FoundationOverview />} />
        <Route path="/business-builder/cheatsheets/subscription-lm-pattern" element={<SubscriptionLmPattern />} />
        <Route path="/business-builder/cheatsheets/surgready-patient-journey" element={<SurgreadyPatientJourney />} />
        <Route path="/cheatsheets/surgready-patient-journey" element={<SurgreadyPatientJourney />} />
        <Route path="/business-builder/cheatsheets/subscription-rewards-pattern" element={<SubscriptionRewardsPattern />} />
        <Route path="/cheatsheets/subscription-rewards-pattern" element={<SubscriptionRewardsPattern />} />
        {/* Energy Research */}
        <Route path="/energy-research" element={<EnergyResearchIndex />} />
        <Route path="/energy-research/australia-oil-reserves" element={<AustraliaOilReserves />} />
        <Route path="/energy-research/australia-oil-sources" element={<AustraliaOilSources />} />
        <Route path="/energy-research/australia-oil-reserves-by-state" element={<AustraliaOilReservesByState />} />
        <Route path="/energy-research/australia-key-leaders" element={<AustraliaKeyLeaders />} />
        <Route path="/energy-research/australia-israel-support" element={<AustraliaIsraelSupport />} />
        <Route path="/energy-research/australia-immediate-actions" element={<AustraliaImmediateActions />} />
        <Route path="/energy-research/australia-f35-companies" element={<AustraliaF35Companies />} />
        <Route path="/energy-research/japan-energy-crisis" element={<JapanEnergyCrisis />} />
        <Route path="/energy-research/australia-decision-makers" element={<AustraliaDecisionMakers />} />
        <Route path="/energy-research/japan-decision-makers" element={<JapanDecisionMakers />} />
        <Route path="/energy-research/key-refineries" element={<KeyRefineries />} />
        <Route path="/energy-research/alternative-supply-routes" element={<AlternativeSupplyRoutes />} />
        <Route path="/energy-research/relationship-map" element={<RelationshipMap />} />
        <Route path="/energy-research/iran-hormuz-update" element={<IranHormuzUpdate />} />
        <Route path="/energy-research/research-checklist" element={<ResearchChecklist />} />
        {/* Legacy redirects from old cheatsheet URLs */}
        <Route path="/cheatsheets/australia-key-leaders" element={<Navigate to="/energy-research/australia-key-leaders" replace />} />
        <Route path="/cheatsheets/australia-israel-support" element={<Navigate to="/energy-research/australia-israel-support" replace />} />
        <Route path="/cheatsheets/australia-immediate-actions" element={<Navigate to="/energy-research/australia-immediate-actions" replace />} />
        <Route path="/cheatsheets/australia-oil-reserves" element={<Navigate to="/energy-research/australia-oil-reserves" replace />} />
        <Route path="/cheatsheets/australia-oil-sources" element={<Navigate to="/energy-research/australia-oil-sources" replace />} />
        <Route path="/cheatsheets/australia-oil-reserves-by-state" element={<Navigate to="/energy-research/australia-oil-reserves-by-state" replace />} />
        <Route path="/cheatsheets/australia-f35-companies" element={<Navigate to="/energy-research/australia-f35-companies" replace />} />
        <Route path="/business-builder/cheatsheets/australia-key-leaders" element={<Navigate to="/energy-research/australia-key-leaders" replace />} />
        <Route path="/business-builder/cheatsheets/australia-israel-support" element={<Navigate to="/energy-research/australia-israel-support" replace />} />
        <Route path="/business-builder/cheatsheets/australia-immediate-actions" element={<Navigate to="/energy-research/australia-immediate-actions" replace />} />
        <Route path="/business-builder/cheatsheets/australia-oil-reserves" element={<Navigate to="/energy-research/australia-oil-reserves" replace />} />
        <Route path="/business-builder/cheatsheets/australia-oil-sources" element={<Navigate to="/energy-research/australia-oil-sources" replace />} />
        <Route path="/business-builder/cheatsheets/australia-oil-reserves-by-state" element={<Navigate to="/energy-research/australia-oil-reserves-by-state" replace />} />
        <Route path="/business-builder/cheatsheets/australia-f35-companies" element={<Navigate to="/energy-research/australia-f35-companies" replace />} />
        <Route path="/business-builder/cheatsheets/seamless-chat-module" element={<SeamlessChatModule />} />
        <Route path="/cheatsheets/seamless-chat-module" element={<SeamlessChatModule />} />
        <Route path="/business-builder/cheatsheets/ai-assistant" element={<AiAssistantCheatsheet />} />
        <Route path="/cheatsheets/ai-assistant" element={<AiAssistantCheatsheet />} />

        {/* Legacy redirects — tenant-management moved off /admin/tenants/* on 2026-08-07 so the
            module id, folder name and URL namespace all match. Kept indefinitely: these paths are
            in Tom's bookmarks, in runbooks, and in #hash deep links into the tenant edit page. */}
        <Route path="/admin/tenants" element={<LegacyTenantRedirect to="/tenant-management" />} />
        <Route path="/admin/tenants/new" element={<LegacyTenantRedirect to="/tenant-management/new" />} />
        <Route path="/admin/tenants/quick-deploy" element={<LegacyTenantRedirect to="/tenant-management/quick-deploy" />} />
        <Route path="/admin/tenants/storage" element={<LegacyTenantRedirect to="/tenant-management/storage" />} />
        <Route path="/admin/tenants/ai-usage" element={<LegacyTenantRedirect to="/tenant-management/ai-usage" />} />
        <Route path="/admin/tenants/sms-usage" element={<LegacyTenantRedirect to="/tenant-management/sms-usage" />} />
        <Route path="/admin/tenants/storage-usage" element={<LegacyTenantRedirect to="/tenant-management/storage-usage" />} />
        <Route path="/admin/tenants/knowledge-folders" element={<LegacyTenantRedirect to="/tenant-management/knowledge-folders" />} />
        <Route path="/admin/tenants/:id/edit" element={<LegacyTenantRedirect to="/tenant-management/:id/edit" />} />
        <Route path="/admin/pay-audit" element={<LegacyTenantRedirect to="/tenant-management/pay-audit" />} />

      </Routes>
      </DocumentTitleManager>
    </>
  );
};

export const App = () => {
  // Check and set tenantId if missing
  React.useEffect(() => {
    const storedTenantId = localStorage.getItem('tenantId');
    if (!storedTenantId) {
      // Use the tenantId from brandConfig as default
      if (brandConfig.tenantId) {
        localStorage.setItem('tenantId', brandConfig.tenantId);
      }
    }
  }, []);

  return (
    <Router>
      <ApolloProvider client={apolloClient}>
        <IpfsGatewayLoader>
          <AuthProvider>
            <ModulePermissionsProvider>
              <ChakraProvider theme={theme}>
                {/* TenantBrandingLoader must sit INSIDE ChakraProvider: it uses
                    useColorMode() to apply the tenant's default colour mode. */}
                <TenantBrandingLoader>
                  <TomProvider>
                    <AuthProfilePrompt />
                  <AppContent />
                  </TomProvider>
                </TenantBrandingLoader>
              </ChakraProvider>
            </ModulePermissionsProvider>
          </AuthProvider>
        </IpfsGatewayLoader>
      </ApolloProvider>
    </Router>
  );
};
